import type {FC} from 'react';
import {
  Box,
  Form as CommonForm,
  FormRow,
  RichTextEditor,
  Select,
  Stack,
  TextField,
  Typography,
} from '@wellstone-solutions/web';
import {states} from 'utils/Forms';
import {CollectionSelect} from './CollectionSelect';
import {SUBTITLE_HELPER_TEXT} from './schema';
import type {ResourceFormBag} from './types';

interface ResourceFormProps {
  form: ResourceFormBag;
  formId: string;
}

const stateItems = states.map((state: string) => ({
  label: state,
  value: state,
}));

export const ResourceForm: FC<ResourceFormProps> = ({form, formId}) => {
  const apiErrors = form.status?.apiErrors ?? {};

  // A field shows its own validation error once touched, and falls back to the
  // API's verification message while untouched.
  const hasFieldError = (fieldName: string): boolean =>
    Boolean(
      (form.touched[fieldName] && form.errors[fieldName]) ||
      (!form.touched[fieldName] && apiErrors[fieldName]),
    );

  const getHelperText = (fieldName: string): string =>
    (form.touched[fieldName] && form.errors[fieldName]) ||
    (!form.touched[fieldName] && apiErrors[fieldName]) ||
    '';

  const locationValue = (
    field: 'address' | 'address2' | 'city' | 'state' | 'zipcode',
  ): string => form.values.location?.[field] ?? '';

  const locationError = (field: string): boolean =>
    Boolean(form.touched.location?.[field] && form.errors.location?.[field]);

  const locationHelperText = (field: string): string =>
    (form.touched.location?.[field] && form.errors.location?.[field]) || '';

  return (
    <CommonForm
      data-testid="resource-form"
      id={formId}
      onSubmit={form.handleSubmit}>
      <Stack direction="column" sx={styles.fields}>
        <FormRow>
          <TextField
            formField="title"
            label="Title"
            value={form.values.title}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={Boolean(form.touched.title && form.errors.title)}
            helperText={(form.touched.title && form.errors.title) || ''}
          />
        </FormRow>

        <FormRow>
          <TextField
            formField="subtitle"
            label="Subtitle (Optional)"
            value={form.values.subtitle}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={Boolean(form.touched.subtitle && form.errors.subtitle)}
            helperText={
              (form.touched.subtitle && form.errors.subtitle) ||
              SUBTITLE_HELPER_TEXT
            }
          />
        </FormRow>

        <Typography variant="h6" mt={2}>
          Description
        </Typography>

        <FormRow>
          <RichTextEditor
            initialValue={form.values.description}
            previewStyle="vertical"
            height="300px"
            hideModeSwitch={false}
            initialEditType="wysiwyg"
            useCommandShortcut={true}
            toolbarItems={[
              ['heading', 'bold', 'italic', 'hr', 'ul', 'ol', 'quote', 'link'],
            ]}
            onChange={(markdown: string) => {
              form.setFieldTouched('description');
              form.setFieldValue('description', markdown);
            }}
          />
        </FormRow>

        <FormRow>
          <CollectionSelect
            value={form.values.collections}
            onChange={(collections) => {
              form.setFieldTouched('collections');
              form.setFieldValue('collections', collections);
            }}
          />
        </FormRow>

        <Typography variant="h6" mt={2}>
          Contact Details
        </Typography>

        <FormRow>
          <TextField
            formField="email"
            label="Email (Optional)"
            value={form.values.email}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={hasFieldError('email')}
            helperText={getHelperText('email')}
          />
        </FormRow>

        <FormRow>
          <TextField
            formField="phoneNumber"
            label="Phone (Optional)"
            value={form.values.phoneNumber}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={hasFieldError('phoneNumber')}
            helperText={getHelperText('phoneNumber')}
          />
        </FormRow>

        <FormRow>
          <TextField
            formField="website"
            label="Website (Optional)"
            value={form.values.website}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={hasFieldError('website')}
            helperText={getHelperText('website')}
          />
        </FormRow>

        <FormRow>
          <TextField
            formField="dateTimeInformation"
            multiline
            minRows={3}
            label="Date & Time Information (Optional)"
            value={form.values.dateTimeInformation}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={Boolean(
              form.touched.dateTimeInformation &&
              form.errors.dateTimeInformation,
            )}
            helperText={
              (form.touched.dateTimeInformation &&
                form.errors.dateTimeInformation) ||
              ''
            }
          />
        </FormRow>

        <Typography variant="h6" mt={2}>
          Location
        </Typography>

        {Boolean(form.values.location?.address) &&
          Boolean(apiErrors.address) && (
            <Typography variant="body2" color="error">
              {apiErrors.address}
            </Typography>
          )}

        <FormRow direction={{xs: 'column', md: 'row'}} spacing={2}>
          <TextField
            fullWidth
            formField="location.address"
            label="Address"
            value={locationValue('address')}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={locationError('address')}
            helperText={locationHelperText('address')}
          />

          <TextField
            fullWidth
            formField="location.address2"
            label="Address 2"
            value={locationValue('address2')}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={locationError('address2')}
            helperText={locationHelperText('address2')}
          />
        </FormRow>

        <FormRow direction={{xs: 'column', md: 'row'}} spacing={2}>
          <TextField
            fullWidth
            formField="location.city"
            label="City"
            value={locationValue('city')}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={locationError('city')}
            helperText={locationHelperText('city')}
          />

          <Box sx={styles.stateSelect}>
            <Select
              containerProps={{fullWidth: true}}
              label="State"
              value={locationValue('state')}
              items={stateItems}
              onChange={(event) => {
                form.setFieldTouched('location.state');
                form.setFieldValue('location.state', event.target.value ?? '');
              }}
            />
          </Box>

          <TextField
            fullWidth
            formField="location.zipcode"
            label="Zip"
            value={locationValue('zipcode')}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={locationError('zipcode')}
            helperText={locationHelperText('zipcode')}
          />
        </FormRow>
      </Stack>
    </CommonForm>
  );
};

const styles = {
  fields: {
    width: {
      xs: '100%',
      lg: '75%',
    },
  },
  stateSelect: {
    width: {
      xs: '100%',
      md: '200px',
    },
  },
};
